import React, { useState, useEffect } from 'react';
import SwiperCore, { Autoplay, EffectCube } from 'swiper';
import CreativePreview from './preview/CreativePreview';
import VideoPreview from './preview/VideoPreview';

SwiperCore.use([Autoplay, EffectCube]);

const UPLOAD_TYPES = [
  { value: 'image', label: 'Cube' },
  { value: 'swiper', label: 'Swiper' },
  { value: 'upload_file', label: 'File Upload' },
  { value: 'video', label: 'Video' },
  { value: 'gif', label: 'GIF' }
];

const TIMELINE_STEPS = [
  {
    title: 'Select Design',
    description: 'Choose your creative type',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    )
  },
  {
    title: 'Upload Assets',
    description: 'Add images and audio',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    )
  },
  {
    title: 'Save Creative',
    description: 'Complete and publish',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    )
  }
];

// --- Subcomponents for major sections ---

function UploadTimeline({ TIMELINE_STEPS, getStepStatus }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between relative">
        {TIMELINE_STEPS.map((step, index) => {
          const stepStatus = getStepStatus();
          const currentStep = ['design', 'upload', 'save'][index];
          const { isActive, isCompleted } = stepStatus[currentStep];
          const isLast = index === TIMELINE_STEPS.length - 1;

          return (
            <React.Fragment key={step.title}>
              <div className="flex flex-col items-center relative z-10">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-xl shadow-green-200'
                    : isActive
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl shadow-blue-200'
                      : 'bg-white border-2 border-slate-200 text-slate-400'
                    }`}
                >
                  {isCompleted ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step.icon
                  )}
                </div>
                <div className="mt-3 text-center">
                  <p className={`font-semibold ${isCompleted
                    ? 'text-green-600'
                    : isActive
                      ? 'text-blue-600'
                      : 'text-slate-500'
                    }`}>
                    {step.title}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">{step.description}</p>
                </div>
              </div>
              {!isLast && (
                <div className="flex-1 h-0.5 relative z-0">
                  <div
                    className={`absolute inset-0 transition-all duration-300 ${isCompleted
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                      : isActive
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600'
                        : 'bg-slate-200'
                      }`}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function VideoFields({ 
  uploadType, 
  isButtonEnabled, 
  setIsButtonEnabled, 
  ctaText, 
  setCtaText, 
  ctaBgColor, 
  setCtaBgColor, 
  ctaTextColor, 
  setCtaTextColor, 
  clickUrl, 
  setClickUrl, 
  buttonUrl, 
  setButtonUrl, 
  isUploading,
  // New props for video URL
  videoInputType,
  setVideoInputType,
  videoUrl,
  setVideoUrl,
  // New props for clearing data
  setSelectedFiles,
  setVideoUrl: setVideoUrlProp,
  errors
}) {
  if (uploadType !== 'video') return null;
  
  // Handler to clear data when switching input types
  const handleInputTypeChange = (newType) => {
    setVideoInputType(newType);
    
    // Clear data when switching
    if (newType === 'file') {
      // Clear video URL when switching to file upload
      setVideoUrl('');
      setVideoUrlProp('');
    } else if (newType === 'url') {
      // Clear selected files when switching to URL input
      setSelectedFiles([]);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Video Input Type Toggle */}
      <div className="space-y-4">
        <label className="block text-slate-700 font-semibold text-sm tracking-wide">
          Video Input Method
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="radio"
              name="videoInputType"
              checked={videoInputType === 'file'}
              onChange={() => handleInputTypeChange('file')}
              disabled={isUploading}
              className="w-4 h-4 text-blue-600 border-2 border-slate-300 focus:ring-4 focus:ring-blue-500/20"
            />
            <span className="text-slate-700 group-hover:text-blue-600 transition-colors duration-200 font-medium">
              Upload Video File
            </span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="radio"
              name="videoInputType"
              checked={videoInputType === 'url'}
              onChange={() => handleInputTypeChange('url')}
              disabled={isUploading}
              className="w-4 h-4 text-blue-600 border-2 border-slate-300 focus:ring-4 focus:ring-blue-500/20"
            />
            <span className="text-slate-700 group-hover:text-blue-600 transition-colors duration-200 font-medium">
              Enter Video URL
            </span>
          </label>
        </div>
      </div>

      {/* Video URL Input */}
      {videoInputType === 'url' && (
        <div className="space-y-2">
          <label className="block text-slate-700 font-semibold text-sm tracking-wide">
            Video URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            className={`w-full rounded-xl border-2 ${errors?.videoUrl ? 'border-red-500' : 'border-slate-200'} focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400`}
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://example.com/video.mp4"
            disabled={isUploading}
          />
          {errors?.videoUrl && <p className="text-red-600 text-sm mt-1">{errors.videoUrl}</p>}
          <p className="text-sm text-slate-500">
            Enter a direct URL to an MP4 or WebM video file
          </p>
        </div>
      )}

      {/* CTA Button fields */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="enableButton"
          checked={isButtonEnabled}
          onChange={(e) => setIsButtonEnabled(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-2 border-slate-300 focus:ring-4 focus:ring-blue-500/20 rounded"
        />
        <label htmlFor="enableButton" className="text-slate-700 font-semibold">
          Show CTA Button
        </label>
      </div>
      {isButtonEnabled && (
        <>
          <div className="space-y-2">
            <label className="block text-slate-700 font-semibold text-sm tracking-wide">
              CTA Text <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`w-full rounded-xl border-2 ${errors?.ctaText ? 'border-red-500' : 'border-slate-200'} focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400`}
              value={ctaText}
              onChange={(e) => setCtaText(e.target.value)}
              placeholder="Enter CTA button text"
              disabled={isUploading}
            />
            {errors?.ctaText && <p className="text-red-600 text-sm mt-1">{errors.ctaText}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                Button URL <span className="text-slate-400">(Optional)</span>
              </label>
              <input
                type="text"
                className={`w-full rounded-xl border-2 ${errors?.buttonUrl ? 'border-red-500' : 'border-slate-200'} focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400`}
                value={buttonUrl}
                onChange={(e) => setButtonUrl(e.target.value)}
                placeholder="https://example.com/button"
                disabled={isUploading}
              />
              {errors?.buttonUrl && <p className="text-red-600 text-sm mt-1">{errors.buttonUrl}</p>}
              <p className="text-sm text-slate-500">URL for CTA button click</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                CTA Background Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  className="h-10 w-20 rounded cursor-pointer"
                  value={ctaBgColor}
                  onChange={(e) => setCtaBgColor(e.target.value)}
                  disabled={isUploading}
                />
                <input
                  type="text"
                  className="flex-1 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400"
                  value={ctaBgColor}
                  onChange={(e) => setCtaBgColor(e.target.value)}
                  placeholder="#000000"
                  disabled={isUploading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                CTA Text Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  className="h-10 w-20 rounded cursor-pointer"
                  value={ctaTextColor}
                  onChange={(e) => setCtaTextColor(e.target.value)}
                  disabled={isUploading}
                />
                <input
                  type="text"
                  className="flex-1 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400"
                  value={ctaTextColor}
                  onChange={(e) => setCtaTextColor(e.target.value)}
                  placeholder="#FFFFFF"
                  disabled={isUploading}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FileUploadSection({ 
  uploadType, 
  fileInputKey, 
  handleFileChange, 
  isUploading, 
  getFileUploadDescription,
  videoInputType, 
  errors,
  uploadFileMode,
  setUploadFileMode,
  genericFileUrls
}) {
  if (uploadType === 'video' && videoInputType === 'url') {
    return null;
  }
  
  return (
    <div className="space-y-4">
      <label className="block text-slate-700 font-semibold text-sm tracking-wide">
        {uploadType === 'upload_file'
          ? (uploadFileMode === 'any' ? 'Select Files (Any)' : 'Select HTML File')
          : uploadType === 'video'
            ? 'Select Video'
            : (uploadType === 'gif' ? 'Select GIFs' : 'Select Images')}
      </label>
      {uploadType === 'upload_file' && (
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input type="radio" name="uploadFileMode" checked={uploadFileMode === 'html'} onChange={() => setUploadFileMode('html')} disabled={isUploading} />
            <span>HTML</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="uploadFileMode" checked={uploadFileMode === 'any'} onChange={() => setUploadFileMode('any')} disabled={isUploading} />
            <span>Any Files (Get URLs)</span>
          </label>
        </div>
      )}
      <div className="relative">
        <input
          key={fileInputKey}
          type="file"
          accept={uploadType === 'upload_file'
            ? (uploadFileMode === 'any' ? '*' : '.html')
            : uploadType === 'video'
              ? '.mp4,.webm'
              : (uploadType === 'gif' ? '.gif' : '.jpg,.jpeg,.png,.webp')}
          multiple={(uploadType !== 'upload_file' && uploadType !== 'gif') || uploadFileMode === 'any'}
          onChange={handleFileChange}
          className={`w-full rounded-xl border-2 border-dashed ${errors?.files ? 'border-red-500' : 'border-slate-300'} hover:border-blue-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 p-6 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`}
          disabled={isUploading}
        />
        <div className="absolute top-3 right-3 text-slate-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
      </div>
      <p className="text-slate-500 text-sm">
        {getFileUploadDescription()}
      </p>
      {errors?.files && <p className="text-red-600 text-sm mt-1">{errors.files}</p>}

      {uploadType === 'upload_file' && uploadFileMode === 'any' && genericFileUrls?.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-slate-700">Uploaded File URLs</h4>
          <ul className="space-y-2">
            {genericFileUrls.map((u, idx) => (
              <li key={idx} className="flex items-center gap-2">
                <span className="text-sm flex-1 break-all">{u.name}</span>
                <button type="button" className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => navigator.clipboard.writeText(u.url)}>Copy</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AudioUploadSection({ uploadType, audioInputKey, handleAudioChange, isUploading, audioFile, removeAudioFile }) {
  if (!(uploadType === 'image' || uploadType === 'swiper' || uploadType === 'gif')) return null;
  return (
    <div className="space-y-4">
      <label className="block text-slate-700 font-semibold text-sm tracking-wide">
        Background Audio <span className="text-slate-400">(Optional)</span>
      </label>
      <div className="relative">
        <input
          key={audioInputKey}
          type="file"
          accept=".mp3,.wav,.aac,.ogg"
          onChange={handleAudioChange}
          className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-green-400 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 transition-all duration-200 p-6 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          disabled={isUploading}
        />
        <div className="absolute top-3 right-3 text-slate-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
        </div>
      </div>
      <p className="text-slate-500 text-sm">
        MP3, WAV, AAC, OGG formats supported. Maximum 10MB.
      </p>
      {audioFile && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-slate-700">{audioFile.file?.name || 'Audio file'}</p>
              <p className="text-sm text-slate-500">
                {audioFile.file ? `${(audioFile.file.size / 1024 / 1024).toFixed(2)} MB` : 'Uploaded'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={removeAudioFile}
            className="text-red-500 hover:text-red-700 transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function LivePreviewSection({ 
  selectedFiles, 
  uploadFileMode,
  uploadType, 
  audioFile, 
  clickUrl, 
  ctaText, 
  ctaBgColor, 
  ctaTextColor, 
  impressionUrl, 
  buttonUrl, 
  isButtonEnabled, 
  removeFile, 
  editId, 
  editSessionKey,
  videoUrl, // New prop
  videoInputType, // New prop
  secondaryImpressionUrl
}) {
  if(uploadFileMode==='any') return;
  // Show preview for video URL even when no files are selected
  if (uploadType === 'video') {
    const videoSource = videoInputType === 'url' ? videoUrl : selectedFiles[0]?.preview;
    
    // Show preview if we have a video source (either URL or file)
    if (videoSource) {
      return (
        <div className="relative mx-auto w-[351px] h-[664px]">
          <VideoPreview
            videoUrl={videoSource}
            ctaText={isButtonEnabled ? ctaText : ''}
            ctaBgColor={ctaBgColor}
            clickUrl={clickUrl}
            ctaTextColor={ctaTextColor}
            impressionUrl={impressionUrl}
            buttonUrl={buttonUrl}
            showButton={isButtonEnabled}
            secondaryImpressionUrl={secondaryImpressionUrl}
          />
          {/* Remove button - only show for file uploads */}
          {videoInputType === 'file' && selectedFiles.length > 0 && (
            <button
              type="button"
              onClick={() => removeFile(0)}
              className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200 z-30 shadow-lg"
            >
              ×
            </button>
          )}
        </div>
      );
    }
    return null; // No video source available
  }
  
  if (uploadType === 'gif') {
    if (selectedFiles.length === 0) return null;
    return (
      <div className="relative mx-auto w-[351px] h-[664px] rounded-[2rem] border-[10px] border-slate-800 shadow-2xl overflow-hidden bg-white">
        <img src={selectedFiles[0].preview || selectedFiles[0].url} alt="gif" className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => removeFile(0)}
          className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200 z-30 shadow-lg"
        >
          ×
        </button>
      </div>
    );
  }

  if (selectedFiles.length === 0) return null;
  if (uploadType === 'upload_file') {
    return (
      <div className="relative mx-auto w-[351px] h-[664px] rounded-[2rem] border-[10px] border-slate-800 shadow-2xl overflow-hidden bg-white">
  <iframe
    src={selectedFiles[0].preview}
    title="HTML Preview"
    className="w-full h-full"
    sandbox="allow-same-origin allow-scripts"
        />
        <button
          type="button"
          onClick={() => removeFile(0)}
          className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200 z-30 shadow-lg"
        >
          ×
        </button>
      </div>
    );
  }
  
  // Use editSessionKey as the key for CreativePreview
  return (
    <CreativePreview
      key={editSessionKey}
      images={selectedFiles}
      audioUrl={audioFile?.preview}
      clickUrl={clickUrl}
      type={uploadType}
      removeFile={removeFile}
      canRemove={true}
    />
  );
}

function ThumbnailReorderGrid({ selectedFiles, setSelectedFiles, uploadType }) {
  if (!(['image', 'swiper'].includes(uploadType)) || selectedFiles.length === 0) return null;

  const handleDragStart = (event, index) => {
    event.dataTransfer.setData('text/plain', String(index));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (event, dropIndex) => {
    event.preventDefault();
    const dragIndexString = event.dataTransfer.getData('text/plain');
    if (dragIndexString === '') return;
    const dragIndex = parseInt(dragIndexString, 10);
    if (Number.isNaN(dragIndex) || dragIndex === dropIndex) return;

    const updated = [...selectedFiles];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setSelectedFiles(updated);
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-700">Selected Images Order</h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {selectedFiles.map((item, index) => (
          <div
            key={item.preview || index}
            className="group relative rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm cursor-move select-none"
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            title={`Drag to reorder: #${index + 1}`}
          >
            <div className="aspect-square w-full bg-slate-100">
              <img
                src={item.preview}
                alt={`Selected  ${index + 1}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </div>
            <div className="absolute top-1 left-1 rounded-md px-2 py-0.5 text-xs font-semibold bg-slate-800/80 text-white">
              {index + 1}
            </div>
            <div className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-slate-900/20 text-white text-xs font-medium">
              Drag
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">Reorder by dragging. This order is used for preview and saving.</p>
    </div>
  );
}

function ActionButtonsSection({ 
  resetForm, isUploading, isSaving, uploadFiles, selectedFiles, saveCreative, editId, audioFile,
  uploadType, videoInputType, videoUrl, shareOption 
}) {
  const hasNewFilesToUpload = selectedFiles.some(f => f.isNew && f.status !== 'success');
  const hasNewAudioToUpload = audioFile && audioFile.isNew && audioFile.status !== 'success';

  let canSave = false;
  if (uploadType === 'video' && videoInputType === 'url') {
    canSave = !!videoUrl && !isUploading && !isSaving;
  } else {
    canSave = selectedFiles.length > 0 && !isUploading && !isSaving && !hasNewFilesToUpload && !hasNewAudioToUpload && selectedFiles.every(f => f.status === 'success');
  }

  return (
    <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-slate-200">
      <button
        type="button"
        className="px-6 py-3 rounded-xl border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 font-medium"
        onClick={resetForm}
        disabled={isUploading || isSaving}
      >
        Clear All
      </button>
      <button
        type="button"
        className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
        onClick={uploadFiles}
        disabled={isUploading || isSaving || (!hasNewFilesToUpload && !hasNewAudioToUpload)}
      >
        {isUploading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            Uploading...
          </div>
        ) : (
          'Upload Files'
        )}
      </button>
      <button
        type="button"
        className="px-6 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
        onClick={saveCreative}
        disabled={!canSave}
      >
        {isSaving ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            {editId ? 'Updating...' : 'Saving...'}
          </div>
        ) : (
          editId ? 'Update Creative' : 'Save Creative'
        )}
      </button>
    </div>
  );
}

function UploadForm({
  creativeName,
  setCreativeName,
  clickUrl,
  setClickUrl,
  buttonUrl,
  setButtonUrl,
  impressionUrl,
  setImpressionUrl,
  uploadType,
  setUploadType,
  selectedFiles,
  setSelectedFiles,
  audioFile,
  setAudioFile,
  isUploading,
  uploadFiles,
  saveCreative,
  editId,
  resetForm,
  handleFileChange,
  handleAudioChange,
  removeFile,
  removeAudioFile,
  fileInputKey,
  audioInputKey,
  // New video-specific props
  ctaText,
  setCtaText,
  ctaBgColor,
  setCtaBgColor,
  ctaTextColor,
  setCtaTextColor,
  isSaving, // Add this prop
  isButtonEnabled,
  setIsButtonEnabled,
  editSessionKey, // <-- add this prop
  // New video URL props
  videoInputType,
  setVideoInputType,
  videoUrl,
  setVideoUrl,
  shareOption,
  setShareOption,
  scriptCode,
  setScriptCode,
  errors,
  uploadFileMode,
  setUploadFileMode,
  genericFileUrls,
  secondaryImpressionUrl,
  setSecondaryImpressionUrl
}) {
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [audioElement, setAudioElement] = useState(null);

  useEffect(() => {
    if (audioFile?.preview) {
      const audio = new Audio(audioFile.preview);
      audio.loop = true;
      setAudioElement(audio);
      return () => {
        audio.pause();
        audio.src = '';
      };
    }
  }, [audioFile]);

  const toggleAudio = () => {
    if (audioElement) {
      if (isAudioMuted) {
        audioElement.play();
      } else {
        audioElement.pause();
      }
      setIsAudioMuted(!isAudioMuted);
    }
  };

  // Updated function to determine active step and completion
  const getStepStatus = () => {
    const steps = {
      design: {
        isActive: false,
        isCompleted: false
      },
      upload: {
        isActive: false,
        isCompleted: false
      },
      save: {
        isActive: false,
        isCompleted: false
      }
    };

    // Design step is completed when we have selected files locally
    if (selectedFiles.length > 0) {
      steps.design.isCompleted = true;
      steps.upload.isActive = true;
    } else {
      steps.design.isActive = true;
    }

    // Upload step is completed when all files are uploaded successfully
    if (selectedFiles.length > 0 && selectedFiles.every(f => f.status === 'success')) {
      steps.upload.isCompleted = true;
      steps.save.isActive = true;
    }

    // Save step is completed when the creative is saved (we can check editId for this)
    if (editId) {
      steps.save.isCompleted = true;
    }

    return steps;
  };

  // Move getFileUploadDescription here so it is defined before use
  const getFileUploadDescription = () => {
    switch (uploadType) {
      case 'video':
        return 'MP4 or WebM format. Maximum 20MB.';
      case 'gif':
        return 'GIF format. Maximum 5MB per file.';
      case 'upload_file':
        return 'Upload single HTML file or switch to Any Files to get URLs.';
      default:
        return 'JPG, PNG, WebP, GIF formats supported. Maximum 5MB per file.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Timeline Component */}
        <UploadTimeline TIMELINE_STEPS={TIMELINE_STEPS} getStepStatus={getStepStatus} />

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden backdrop-blur-sm">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {editId ? 'Edit Creative' : 'Create New Creative'}
              </h2>
              <p className="text-blue-100 text-sm">Upload and configure your webview creative</p>
            </div>
            {editId && (
              <button
                className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-4 py-2 text-white hover:bg-white/30 transition-all duration-200 font-medium"
                onClick={resetForm}
              >
                Cancel Edit
              </button>
            )}
          </div>

          <div className="p-8 space-y-8">
            {/* Creative Name */}
            <div className="space-y-2">
              <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                Creative Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={`w-full rounded-xl border-2 ${errors?.creativeName ? 'border-red-500' : 'border-slate-200'} focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400`}
                value={creativeName}
                onChange={(e) => setCreativeName(e.target.value)}
                placeholder="Enter a descriptive name for your creative"
                disabled={isUploading}
              />
              {errors?.creativeName && <p className="text-red-600 text-sm mt-1">{errors.creativeName}</p>}
            </div>

            {(uploadType === 'image' || uploadType === 'swiper' || uploadType === 'video' || uploadType === 'gif') && (
              <div className="space-y-6">
                {/* Row 1: Click URL */}
                <div className="space-y-2">
                  <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                    Click URL <span className="text-slate-400">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400"
                    value={clickUrl}
                    onChange={(e) => setClickUrl(e.target.value)}
                    placeholder="https://example.com/click"
                    disabled={isUploading}
                  />
                </div>

                {/* Row 2: Impression URLs side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Primary Impression URL */}
                  <div className="space-y-2">
                    <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                      Primary Impression URL <span className="text-slate-400">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full rounded-xl border-2 ${errors?.impressionUrl ? 'border-red-500' : 'border-slate-200'} focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400`}
                      value={impressionUrl}
                      onChange={(e) => setImpressionUrl(e.target.value)}
                      placeholder="https://example.com/impression"
                      disabled={isUploading}
                    />
                    {errors?.impressionUrl && (
                      <p className="text-red-600 text-sm mt-1">{errors.impressionUrl}</p>
                    )}
                  </div>

                  {/* Secondary Impression URL */}
                  <div className="space-y-2">
                    <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                      Secondary Impression URL <span className="text-slate-400">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full rounded-xl border-2 ${errors?.secondaryImpressionUrl ? 'border-red-500' : 'border-slate-200'} focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 px-4 py-3 text-slate-700 placeholder-slate-400`}
                      value={secondaryImpressionUrl}
                      onChange={(e) => setSecondaryImpressionUrl(e.target.value)}
                      placeholder="https://example.com/secondary-impression"
                      disabled={isUploading}
                    />
                    {errors?.secondaryImpressionUrl && (
                      <p className="text-red-600 text-sm mt-1">{errors.secondaryImpressionUrl}</p>
                    )}
                  </div>
                </div>
              </div>
            )}



            {/* Upload Type */}
            <div className="space-y-4">
              <label className="block text-slate-700 font-semibold text-sm tracking-wide">Upload Type</label>
              <div className="flex gap-4">
                {UPLOAD_TYPES.map(opt => (
                  <label key={opt.value} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="uploadType"
                      checked={uploadType === opt.value}
                      onChange={() => { setUploadType(opt.value); setSelectedFiles([]); setScriptCode('') }}
                      disabled={isUploading}
                      className="w-4 h-4 text-blue-600 border-2 border-slate-300 focus:ring-4 focus:ring-blue-500/20"
                    />
                    <span className="text-slate-700 group-hover:text-blue-600 transition-colors duration-200 font-medium">
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Video-specific fields */}
            <VideoFields
              uploadType={uploadType}
              isButtonEnabled={isButtonEnabled}
              setIsButtonEnabled={setIsButtonEnabled}
              ctaText={ctaText}
              setCtaText={setCtaText}
              ctaBgColor={ctaBgColor}
              setCtaBgColor={setCtaBgColor}
              ctaTextColor={ctaTextColor}
              setCtaTextColor={setCtaTextColor}
              clickUrl={clickUrl}
              setClickUrl={setClickUrl}
              buttonUrl={buttonUrl}
              setButtonUrl={setButtonUrl}
              isUploading={isUploading}
              videoInputType={videoInputType}
              setVideoInputType={setVideoInputType}
              videoUrl={videoUrl}
              setVideoUrl={setVideoUrl}
              setSelectedFiles={setSelectedFiles}
              errors={errors}
            />

            {/* File Upload */}
            <FileUploadSection
              uploadType={uploadType}
              fileInputKey={fileInputKey}
              handleFileChange={handleFileChange}
              isUploading={isUploading}
              getFileUploadDescription={getFileUploadDescription}
              videoInputType={videoInputType}
              errors={errors}
              uploadFileMode={typeof uploadFileMode !== 'undefined' ? uploadFileMode : 'html'}
              setUploadFileMode={typeof setUploadFileMode !== 'undefined' ? setUploadFileMode : () => {}}
              selectedFiles={selectedFiles}
              genericFileUrls={typeof genericFileUrls !== 'undefined' ? genericFileUrls : []}
            />

            {(uploadType === 'image' || uploadType === 'swiper' || uploadType === 'gif') && (
              <AudioUploadSection
                uploadType={uploadType}
                audioInputKey={audioInputKey}
                handleAudioChange={handleAudioChange}
                isUploading={isUploading}
                audioFile={audioFile}
                removeAudioFile={removeAudioFile}
              />
            )}

            {/* Share Option */}
            {(uploadType === 'image' || uploadType === 'swiper' || uploadType === 'video' || uploadType === 'gif') && (
              <>
              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="shareOption"
                  checked={!!shareOption}
                  onChange={e => setShareOption(e.target.checked ? 1 : 0)}
                  className="mr-2"
                />
                <label htmlFor="shareOption" className="font-medium text-gray-700">Share</label>
              </div>
   
              {/* Custom Script */}
              <div className="space-y-4">
                <label className="block text-slate-700 font-semibold text-sm tracking-wide">
                  Custom Script <span className="text-slate-400">(Optional)</span>
                </label>
                <textarea
                  className="w-full rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/10 transition-all duration-200 p-4 font-mono text-sm bg-gray-50"
                  rows={4}
                  value={scriptCode}
                  onChange={(e) => setScriptCode(e.target.value)}
                  placeholder="Enter custom JS script"
                  disabled={isUploading || isSaving}
                />
                <p className="text-sm text-slate-500">
                  Add JavaScript to run inside the WebView.
                </p>
              </div>
              </>
            )}

            {/* Thumbnails & Reorder for image/swiper (not gif) */}
            {(['image', 'swiper'].includes(uploadType)) && selectedFiles.length > 0 && (
              <ThumbnailReorderGrid
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                uploadType={uploadType}
              />
            )}

            {/* Live Preview */}
            {(selectedFiles.length > 0 || (uploadType === 'video' && videoInputType === 'url' && videoUrl)) && (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Live Preview
                </h3>
                <div className="flex justify-center bg-gradient-to-b from-slate-100 to-slate-200 rounded-2xl p-8">
                <LivePreviewSection
                    selectedFiles={selectedFiles}
                    uploadType={uploadType}
                    uploadFileMode={uploadFileMode}
                    audioFile={audioFile}
                    clickUrl={clickUrl}
                    ctaText={isButtonEnabled ? ctaText : ''}
                    ctaBgColor={ctaBgColor}
                    ctaTextColor={ctaTextColor}
                    impressionUrl={impressionUrl}
                    buttonUrl={buttonUrl}
                    isButtonEnabled={isButtonEnabled}
                    removeFile={removeFile}
                    editId={editId}
                    editSessionKey={editSessionKey}
                    videoUrl={videoUrl}
                    videoInputType={videoInputType}
                    secondaryImpressionUrl={secondaryImpressionUrl}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <ActionButtonsSection
              resetForm={resetForm}
              isUploading={isUploading}
              isSaving={isSaving}
              uploadFiles={uploadFiles}
              selectedFiles={selectedFiles}
              saveCreative={saveCreative}
              editId={editId}
              audioFile={audioFile}
              uploadType={uploadType} // <-- add
              videoInputType={videoInputType} // <-- add
              videoUrl={videoUrl} // <-- add
              shareOption={shareOption} // <-- add
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadForm;